/**
 * packages/server/src/settings.ts
 *
 * System Settings — 범용 환경변수 관리 (Convex 스타일).
 *
 * _system_settings 테이블은 DB migration 없이 자동 생성 (CREATE TABLE IF NOT EXISTS).
 * 시스템 내부 테이블이므로 사용자 스키마(schema.ts)에 포함하지 않는다.
 *
 * Admin API:
 *   GET    /_admin/settings/env-vars           → 전체 목록 (.env + DB, 값 마스킹)
 *   POST   /_admin/settings/env-vars           → 추가/수정 { name, value }
 *   DELETE /_admin/settings/env-vars/:name     → 삭제
 */
import { Hono } from "hono";
import { encrypt, decrypt, maskKey } from "./crypto.js";
import { applyAdminAccessMiddleware, type AdminAccessPolicy } from "./server-admin-access.js";

// ─── Types ──────────────────────────────────────────────────

interface SettingsRow {
  key: string;
  value: string;
  iv: string;
  created_at: string;
  updated_at: string;
}

export interface EnvVarInfo {
  name: string;
  source: "env" | "db" | "both";
  maskedValue: string;
}

// rawSql 함수 타입 — database.ts에서 전달받음
type RawSqlFn = (sql: string, params?: unknown[]) => Promise<unknown[]>;

// ─── DB Table (auto-created) ────────────────────────────────

// 현재 스키마(search_path 기준)에 _system_settings 테이블이 없으면 생성.
// 일반 CREATE TABLE IF NOT EXISTS는 search_path의 모든 스키마를 탐색하므로,
// public 스키마에 동명 테이블이 있으면 앱 스키마에 생성하지 않는 문제가 있음.
// 이를 방지하기 위해 current_schema() 기준으로 명시적 체크.
const CREATE_TABLE_SQL = `
    DO $$
    BEGIN
        IF NOT EXISTS (
            SELECT 1 FROM information_schema.tables
            WHERE table_schema = current_schema()
            AND table_name = '_system_settings'
        ) THEN
            CREATE TABLE _system_settings (
                key TEXT PRIMARY KEY,
                value TEXT NOT NULL,
                iv TEXT NOT NULL,
                created_at TIMESTAMP DEFAULT NOW(),
                updated_at TIMESTAMP DEFAULT NOW()
            );
        END IF;
    END$$;
`;

let tableInitialized = false;

async function ensureTable(rawSql: RawSqlFn): Promise<void> {
  if (tableInitialized) return;
  await rawSql(CREATE_TABLE_SQL);
  tableInitialized = true;
}

// ─── CRUD ───────────────────────────────────────────────────

async function getAllSettings(rawSql: RawSqlFn): Promise<SettingsRow[]> {
  await ensureTable(rawSql);
  return (await rawSql(
    "SELECT key, value, iv, created_at::text, updated_at::text FROM _system_settings ORDER BY key",
  )) as SettingsRow[];
}

async function upsertSetting(rawSql: RawSqlFn, key: string, plainValue: string): Promise<void> {
  await ensureTable(rawSql);
  const { encrypted, iv } = encrypt(plainValue);
  await rawSql(
    `INSERT INTO _system_settings (key, value, iv, updated_at)
         VALUES ($1, $2, $3, NOW())
         ON CONFLICT (key) DO UPDATE SET value = $2, iv = $3, updated_at = NOW()`,
    [key, encrypted, iv],
  );
}

async function deleteSetting(rawSql: RawSqlFn, key: string): Promise<boolean> {
  await ensureTable(rawSql);
  const rows = (await rawSql("SELECT key FROM _system_settings WHERE key = $1", [key])) as SettingsRow[];
  if (rows.length === 0) return false;
  await rawSql("DELETE FROM _system_settings WHERE key = $1", [key]);
  return true;
}

// ─── Load All DB Vars → process.env ─────────────────────────

/**
 * 서버 시작 시 DB의 모든 환경변수를 process.env에 주입.
 * 이미 .env에 있는 값은 덮어쓰지 않음 (.env 우선).
 */
export async function loadDbEnvVars(rawSql: RawSqlFn): Promise<number> {
  const rows = await getAllSettings(rawSql);
  let loaded = 0;

  for (const row of rows) {
    if (!process.env[row.key]) {
      try {
        process.env[row.key] = decrypt(row.value, row.iv);
        loaded++;
      } catch {
        // 복호화 실패 (secret.key 변경 등) → 스킵
        console.warn(`[settings] Failed to decrypt ${row.key} — skipping`);
      }
    }
  }

  return loaded;
}

// ─── Env Var Collection (for API response) ──────────────────

/**
 * .env 파일 변수 목록 수집.
 * CLI의 buildEnv에서 로드한 .env 변수들은 이미 process.env에 있지만,
 * "어떤 것이 .env에서 온 건지" 구분하려면 .env 파일을 직접 파싱해야 한다.
 *
 * 여기서는 간략하게: DB에 저장된 키와 process.env를 비교하여 source 구분.
 */

// ─── Admin Routes ───────────────────────────────────────────

export function createSettingsRoutes(rawSql: RawSqlFn, options?: { accessPolicy?: AdminAccessPolicy }): Hono {
  const settings = new Hono();
  applyAdminAccessMiddleware(settings, options?.accessPolicy);

  // GET /_admin/settings/env-vars — 전체 목록 (.env + DB 병합, 값 마스킹)
  settings.get("/env-vars", async (c) => {
    await ensureTable(rawSql);

    const dbRows = await getAllSettings(rawSql);
    const dbKeys = new Set(dbRows.map((r) => r.key));
    const result: EnvVarInfo[] = [];

    // 1. DB에 저장된 변수
    for (const row of dbRows) {
      let maskedValue = "••••••••";
      try {
        const plainValue = decrypt(row.value, row.iv);
        maskedValue = maskKey(plainValue);
      } catch {
        /* 복호화 실패 */
      }

      // .env에도 같은 키가 있으면 "both" (env 우선)
      const envValue = getOriginalEnvValue(row.key);
      result.push({
        name: row.key,
        source: envValue !== undefined ? "both" : "db",
        maskedValue: envValue !== undefined ? maskKey(envValue) : maskedValue,
      });
    }

    // 2. .env에만 있는 변수 (DB에 없는 것)
    //    process.env는 너무 많으므로, 주요 패턴만 표시
    for (const [key, value] of Object.entries(envSnapshot)) {
      if (!dbKeys.has(key) && value !== undefined) {
        result.push({
          name: key,
          source: "env",
          maskedValue: maskKey(value),
        });
      }
    }

    return c.json(result);
  });

  // POST /_admin/settings/env-vars — 추가/수정
  settings.post("/env-vars", async (c) => {
    const body = (await c.req.json()) as { name?: string; value?: string };
    const { name, value } = body;

    if (!name || value === undefined || value === null) {
      return c.json({ error: "name and value are required" }, 400);
    }

    // 이름 유효성 검사 (환경변수 네이밍 규칙)
    if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(name)) {
      return c.json({ error: "Invalid variable name. Use letters, numbers, and underscores only." }, 400);
    }

    await upsertSetting(rawSql, name, value);

    // 즉시 process.env에도 반영 (서버 재시작 없이 사용 가능)
    process.env[name] = value;

    return c.json({
      success: true,
      name,
      maskedValue: maskKey(value),
    });
  });

  // DELETE /_admin/settings/env-vars/:name — 삭제
  settings.delete("/env-vars/:name", async (c) => {
    const name = c.req.param("name");
    const deleted = await deleteSetting(rawSql, name);

    if (!deleted) {
      return c.json({ error: "No stored variable with this name" }, 404);
    }

    // process.env에서도 제거 (.env에 있으면 원래 값 복원)
    const envOriginal = envSnapshot[name];
    if (envOriginal !== undefined) {
      process.env[name] = envOriginal; // .env 원래 값 복원
    } else {
      delete process.env[name];
    }

    return c.json({ success: true, name });
  });

  return settings;
}

// ─── System Variable Protection ─────────────────────────────
//
// 시스템 환경변수는 서버 내부에서만 사용하고 사용자 코드에 노출하면 안 된다.
// DATABASE_URL에는 DB 비밀번호가 포함되어 있어, 유출 시 다른 테넌트 데이터 접근 가능.
//
// 방어 전략:
// 1. captureEnvSnapshot()에서 시스템 변수를 스냅샷에 포함하지 않음 (Dashboard 미노출)
// 2. sanitizeSystemEnvVars()에서 process.env에서 삭제 (사용자 코드 접근 차단)

/** 시스템 변수 — process.env에서 삭제 + Dashboard에 노출하지 않음 */
const SYSTEM_ENV_KEYS = [
  "DATABASE_URL", // DB 비밀번호 포함 → 유출 시 다른 테넌트 접근 가능
  "GENCOW_SYSTEM_DATABASE_URL", // system-plane DB 비밀번호 포함 → 유출 시 다른 테넌트/시스템 접근 가능
  "GENCOW_SYSTEM_USER_PASS", // system-plane role password
  "ANALYTICS_DATABASE_URL", // Analytics 전용 DB 비밀번호 포함 → 서버 내부 store만 사용
  "GENCOW_WAKE_SIGNAL_DATABASE_URL", // LISTEN 전용 DB URL → DB 비밀번호 포함
  "GENCOW_REALTIME_OUTBOX_POLL_INTERVAL_MS", // BaaS realtime worker 운영 설정
  "GENCOW_WORKFLOW_RECONCILER_INTERVAL_MS", // BaaS workflow/RAG wake fallback 운영 설정
  "BETTER_AUTH_SECRET", // 인증 토큰 위조 가능
  "BETTER_AUTH_URL", // 내부 인증 URL
  "PLATFORM_OPENAI_KEY", // 플랫폼 전용 AI 키
  "PLATFORM_GOOGLE_KEY", // 플랫폼 전용 Gemini/OCR 키
  "PLATFORM_GOOGLE_CLIENT_ID", // 플랫폼 전용 Google OAuth client
  "PLATFORM_GOOGLE_CLIENT_SECRET", // 플랫폼 전용 Google OAuth secret
  "GENCOW_DOCUMENT_CUSTOM_VLM_TOKEN", // 플랫폼 전용 커스텀 VLM 토큰
  "GENCOW_DOCUMENT_CUSTOM_VLM_HEADERS_JSON", // 플랫폼 전용 커스텀 VLM 인증 헤더
  "RESEND_API_KEY", // 플랫폼 이메일 발송 키 → server bootstrap에서 캡처한 뒤 삭제
  // NOTE: IS_PLATFORM은 런타임 내내 참조 (subdomain proxy, provisioner 등) → 삭제 불가
];

/** 시스템 변수 패턴 — Dashboard에 노출하지 않음 (삭제는 안 함) */
const SYSTEM_ENV_PATTERNS = [
  /^GENCOW_/, // 내부 시스템 설정
  /^(MAX_CONCURRENT_RUNNING|MAX_CONCURRENT_WAKE|MAX_WAKE_QUEUE_MS)$/,
  /^(MIN_AVAILABLE_RAM_MB|WAKE_RETRY_AFTER_SEC|EVICTION_THRESHOLD_MB)$/,
  /^(WARM_POOL_MIN_IDLE|WARM_POOL_MAX|WARM_POOL_BACKEND|WARM_POOL_DISABLED)$/,
  /^(DEPLOY_CANDIDATE_RESERVE|COWBOX_WARM_POOL_PORT_RANGE)$/,
  /^BUN_JSC_/, // Bun 런타임 설정
  /^NODE_PATH$/, // 모듈 경로
  /^NEXT_PUBLIC_/, // 프론트엔드용
  /^PORT$/, // 서버 포트
];

// ─── Env Snapshot ───────────────────────────────────────────
// 서버 시작 시점의 .env 변수 캡처 (CLI buildEnv에서 로드한 값)
// DB 변수 로드 전에 호출하여, .env 원본 값을 보존.

const envSnapshot: Record<string, string> = {};
const CAPTURE_PLATFORM_PROVIDER_SECRET_ENV_SNAPSHOT_KEY = "__gencowCapturePlatformProviderSecretEnvSnapshot";

/**
 * 서버 시작 시 호출 — .env에서 로드된 환경변수를 스냅샷.
 * 시스템 변수는 포함하지 않음 (Dashboard에 노출 방지).
 */
export function captureEnvSnapshot(): void {
  // _API_KEY, _SECRET, _TOKEN 등 사용자가 관심있는 패턴만 캡처
  const patterns = [
    /_API_KEY$/i,
    /_SECRET$/i,
    /_TOKEN$/i,
    /_URL$/i,
    /_DSN$/i,
    /_KEY$/i,
    /_PASSWORD$/i,
    /^DATABASE_/,
    /^REDIS_/,
    /^OPENAI_/,
    /^ANTHROPIC_/,
    /^GOOGLE_/,
    /^AZURE_/,
    /^AWS_/,
    /^STRIPE_/,
    /^WEBHOOK_/,
    /^SMTP_/,
    /^MAIL_/,
  ];

  // 시스템 변수 완전 제외 (키 이름 + 패턴 둘 다 체크)
  const systemKeySet = new Set(SYSTEM_ENV_KEYS);

  for (const [key, value] of Object.entries(process.env)) {
    if (!value) continue;
    if (systemKeySet.has(key)) continue;
    if (SYSTEM_ENV_PATTERNS.some((p) => p.test(key))) continue;
    if (patterns.some((p) => p.test(key))) {
      envSnapshot[key] = value;
    }
  }
}

/**
 * DB 연결 완료 후 호출 — process.env에서 시스템 변수를 삭제.
 * 사용자 함수에서 process.env.DATABASE_URL 등에 접근할 수 없게 된다.
 *
 * 안전성:
 * - postgres.js는 연결 시점에만 URL을 읽고 이후엔 TCP 소켓만 유지
 * - better-auth도 초기화 시점에 시크릿을 읽고 이후엔 메모리에 보관
 * - 삭제 후에도 기존 연결/인증은 정상 동작
 */
const CAPTURE_AI_STREAM_CONFIG_SNAPSHOT_KEY = "__gencowCaptureAiStreamConfigSnapshot";

export function sanitizeSystemEnvVars(): void {
  const captureProviderSecrets = (globalThis as Record<string, unknown>)[
    CAPTURE_PLATFORM_PROVIDER_SECRET_ENV_SNAPSHOT_KEY
  ];
  if (typeof captureProviderSecrets === "function") {
    captureProviderSecrets(process.env);
  }

  const captureAiStreamConfig = (globalThis as Record<string, unknown>)[CAPTURE_AI_STREAM_CONFIG_SNAPSHOT_KEY];
  if (typeof captureAiStreamConfig === "function") {
    captureAiStreamConfig(process.env);
  }

  let sanitizedCount = 0;
  for (const key of SYSTEM_ENV_KEYS) {
    if (process.env[key] !== undefined) {
      delete process.env[key];
      sanitizedCount++;
    }
  }
  if (sanitizedCount > 0) {
    console.log(`[security] ${sanitizedCount} system env var(s) removed from process.env ✓`);
  }
}

function getOriginalEnvValue(key: string): string | undefined {
  return envSnapshot[key];
}
